<script lang="ts" setup>
import type { HeaderGradeTypeProps } from '~/models/Subject'
import type { BaseItem } from '~/models/Content/BaseItem'
import type { ViewMode } from '~/models/Content/BaseField'
import { useI18n } from 'vue-i18n'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { faArrowLeft } from '@fortawesome/duotone-regular-svg-icons'
import { useRoute, useRouter } from '#imports'
import { firstOf } from '~/utils/queryUtils'
import { forceColorShade } from '~/utils/colors'
import useProductStore from '~/stores/product'
import { useAuthStore } from '~/stores/auth'
import { PendoFeature } from '~/models/Pendo'
import { ColorTheme } from '~/models/Content/Color'
import { useMedia } from '~/composables/useMedia'
import useGradeString from '~/composables/useGradeString'
import { useContentHelper } from '~/composables/useContentHelper'
import { useColorTheme } from '~/composables/useColorTheme'
import useCardStyles from '~/composables/useCardStyles'
import { useAppColor } from '~/composables/useAppColor'
import useAccordion from '~/composables/useAccordion'
import RichTextRenderer from '~/components/utils/RichTextRenderer.vue'
import HeaderViewBannerGrid from '~/components/utils/HeaderViewBannerGrid.vue'
import CopyrightButton from '~/components/utils/CopyrightButton.vue'
import Button from '~/components/ui/Button.vue'
import HeaderTeacherButton from '~/components/subject/HeaderTeacherButton.vue'
import HeaderStencil from '~/components/subject/HeaderStencil.vue'
import Header8To10Section from '~/components/subject/gradetypes/Header8To10Section.vue'
import GradeFilter from '~/components/ResourceFinder/GradeFilter.vue'
import Image from '~/components/media/Image.vue'
import SimpleCard from '~/components/cards/SimpleCard.vue'
import GridCard from '~/components/cards/GridCard.vue'

const props = withDefaults(defineProps<HeaderGradeTypeProps>(), {
  subjectRoute: 'subject',
  gradeFilter: false,
})

const accordionIds = ref<number[]>([])

const { t } = useI18n()
const { isTeacher } = storeToRefs(useAuthStore())
const { hasLoaded: hasLoadedProducts } = storeToRefs(useProductStore())
const { set: setAppColor, clear: clearAppColor } = useAppColor()
const { isContentHeader } = useContentHelper()
const { theme } = useColorTheme(computed(() => props.header?.colorTheme ?? ColorTheme.BlueOrange))
const { cardStyle, themeStyle } = useCardStyles(theme)
const { getAccordion } = useAccordion()
const { createGradeString } = useGradeString()

const route = useRoute()
const router = useRouter()

const mediaContentId = computed(() => props.header?.backgroundMedia && Number(props.header.backgroundMedia.destinationContentId))
const { data: bgMedia, isLoading: bgMediaIsLoading } = firstOf(useMedia(computed(() => mediaContentId.value ? [mediaContentId.value] : [])))

const showTeacherButton = computed(() => isTeacher.value && hasLoadedProducts.value && props.header)
const isCollapsible = computed(() => Boolean(props.header && isContentHeader(props.header) && props.header.collapsible))
const showGradeFilter = computed(() => props.gradeFilter && (props.header?.grades?.length ?? 0) > 1)

const viewMode = computed((): ViewMode => (props?.header && isContentHeader(props.header) && props.header.viewMode) || 'default')
const isGrid = computed(() => viewMode.value === 'grid')

const headerStyle = computed(() => {
  const id = props.header?.stencil?.identifier
  return (id && id !== 'none') && { background: forceColorShade(theme.value[60], '30') }
})

onMounted(() => setAppColor({
  themeColor: theme.value[60],
  bgColor: theme.value[10],
}))

onBeforeUnmount(() => clearAppColor())

watch(() => props.header, () => {
  if (!props.header)
    return
  updateAccordion(props.header.locationId)
}, { immediate: true, deep: true })

watch(() => route.hash, () => {
  if (props.header)
    accordionIds.value = getAccordion(props.header.locationId)
}, { immediate: true })

function updateAccordion(headerLocationId: number) {
  const [locationId] = getAccordion(headerLocationId)
  const locationIndex = locationId
    ? props.packages.findIndex(content => locationId === content.locationId)
    : -1

  const accordionNr = Number(route.hash || (locationIndex + 1))
  if (accordionNr < 1)
    return

  router
    .replace({ ...route, hash: `#${accordionNr}` })
    .then(() => nextTick())
    .then(() => {
      setTimeout(() => {
        document
          .getElementById(`${accordionNr}`)
          ?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest',
          })
      }, 100) // wait for transition of accordion
    })
}

function isViewMode(item: BaseItem, viewMode: ViewMode) {
  return isContentHeader(item) && item.viewMode === viewMode
}
</script>

<template>
  <div
    :class=" { 'gap-20': !isCollapsible }"
    :style="themeStyle"
    class="grid text-(--theme-60)"
  >
    <HeaderViewBannerGrid
      v-if="props.header"
      class="bg-(--theme-60) text-(--theme-10)"
    >
      <Teleport to="#header-navigation">
        <Button
          is="router-link"
          v-if="subjectCode"
          :color="theme[50]"
          :to="{ name: props.subjectRoute, params: { subject: subjectCode.toLowerCase() } }"
          size="xl"
          variant="outline"
          shade="light"
        >
          <font-awesome-icon :icon="faArrowLeft" />
          {{ t(`metadata.subjects.${subjectCode}`) }}
        </Button>
        <GradeFilter
          v-if="showGradeFilter"
          :color="theme[50]"
          :grade-codes="header?.grades ?? []"
        />
      </Teleport>
      <div class="grid-in-header-info flex flex-col items-start gap-4 self-center py-24 md:py-32">
        <p
          v-if="!showGradeFilter && header?.grades?.length"
          class="text-sm font-medium tracking-wider uppercase"
          v-text="createGradeString(header.grades)"
        />
        <h1
          class="text-4xl font-black"
          v-text="props.header.title"
        />
        <div
          v-if="props.header.intro"
          class="prose space-y-2 text-(--theme-10)"
        >
          <RichTextRenderer
            :text="props.header.intro"
            class="prose text-xl text-[currentColor]"
          />
        </div>
        <HeaderTeacherButton
          v-if="showTeacherButton"
          :data-pendo="PendoFeature.TeacherContent.Header"
          :selected-header="props.header"
        />
      </div>
      <div
        :style="headerStyle"
        class="grid-in-image md:grid-in-image-top-full relative size-full"
      >
        <HeaderStencil
          v-if="header?.stencil?.identifier"
          :stencil="header.stencil.identifier"
          class="absolute left-0 z-10"
        />
        <Image
          v-if="mediaContentId"
          :class="!bgMediaIsLoading && 'animate-fade'"
          :content="mediaContentId"
          class="aspect-video !h-auto min-h-full w-full max-w-full place-self-end object-cover object-left md:h-0 "
        />
      </div>
      <CopyrightButton
        v-if="bgMedia && 'copyright' in bgMedia && bgMedia.copyright"
        :copyright="bgMedia.copyright"
        class="col-end-[image] row-start-2 m-4 place-self-end"
      />
    </HeaderViewBannerGrid>
    <section
      v-for="(section, index) in packages"
      :key="section.contentId"
      :class="{
        'border-b-1 border-(--theme-20)': isCollapsible,
        'bg-gradient-to-t from-(--theme-20) from-1% to-transparent to-80% transition': isCollapsible && !accordionIds.includes(section.locationId),
        'last:pb-20': !isCollapsible && !contents.length,
      }"
      :style="isContentHeader(section) && cardStyle(section)"

      class="grid grid-cols-1 place-items-center"
    >
      <div class="max-w-screen-au flex w-full flex-col gap-24 px-4 sm:px-8">
        <Header8To10Section
          v-if="header"
          :header="header"
          :section="section"
          :index="index"
          :hash="route.hash"
          :is-collapsible="isCollapsible"
          :grade-filter="showGradeFilter"
          @open-accordion="accordionIds = $event"
        />
      </div>
    </section>
    <section
      v-if="contents.length > 0"
      :class="{ 'pt-20': isCollapsible }"
      class="max-w-screen-au mx-auto flex w-full flex-col gap-4 px-4 pb-20 sm:px-8"
    >
      <h2
        v-if="packages.length > 0"
        class="font-tiempos text-4xl"
        v-text="t('labels.other')"
      />
      <ul v-if="header" class="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
        <li
          v-for="content in contents"
          :key="content.locationId"
          class="flex"
        >
          <GridCard
            v-if="isGrid"
            :loading="!packages.length ? 'eager' : 'lazy'"
            :resource="content"
          />
          <SimpleCard
            v-else
            :resource="content"
          />
        </li>
      </ul>
    </section>
  </div>
</template>
